import {
  AccessibilityIcon,
  BanknoteIcon,
  ExternalLinkIcon,
  HeartPulseIcon,
  MailIcon,
  PackageSearchIcon,
  PhoneIcon,
  UserRoundIcon,
} from "lucide-react";
import { InformationText } from "@/components/app/information-text";
import { Card } from "@/components/ui/card";
import { useT } from "@/i18n";
import { eventWhen } from "@/i18n/format";
import {
  informationContactHref,
  informationLinkSite,
  informationUrl,
  TOURNAMENT_DETAIL_GROUPS,
  type TournamentDetails,
} from "../../../shared/tournament-information";
import { InfoDisclosure } from "./InfoDisclosure";

export function TournamentKeyTimes({ details }: { details: TournamentDetails }) {
  const t = useT();
  const fields =
    TOURNAMENT_DETAIL_GROUPS.find((group) => group.key === "keyTimes")?.fields.filter(
      (field) => details[field.key],
    ) ?? [];
  if (!fields.length) return null;
  return (
    <section className="border-t px-5 py-5 adm:px-6" aria-label={t("eventInfo.group.keyTimes")}>
      <dl className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-x-10 sm:gap-y-5">
        {fields.map((field) => {
          const value = details[field.key] ?? "";
          return (
            <div
              key={field.key}
              className="flex items-baseline justify-between gap-4 sm:block sm:flex-1"
            >
              <dt className="text-sm text-muted-foreground">
                {t(`eventInfo.detail.${field.key}`)}
              </dt>
              <dd className="shrink-0 text-right sm:mt-2 sm:text-left">
                <time dateTime={value}>
                  <span className="block text-2xl font-semibold tabular-nums tracking-tight">
                    {value.slice(11, 16)}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {eventWhen(value.slice(0, 10))}
                  </span>
                </time>
              </dd>
            </div>
          );
        })}
      </dl>
      <p className="mt-4 text-xs text-muted-foreground">{t("eventInfo.localTime")}</p>
    </section>
  );
}

export function TournamentContacts({ details }: { details: TournamentDetails }) {
  const t = useT();
  const groups = TOURNAMENT_DETAIL_GROUPS.filter(
    (group) =>
      (group.key === "organiser" || group.key === "firstAid" || group.key === "lostFound") &&
      group.fields.some((field) => details[field.key]),
  );
  if (!groups.length) return null;
  return (
    <Card className="min-w-0 gap-0 divide-y p-0">
      {groups.map((group) => {
        const Icon =
          group.key === "firstAid"
            ? HeartPulseIcon
            : group.key === "lostFound"
              ? PackageSearchIcon
              : UserRoundIcon;
        return (
          <section key={group.key} className="min-w-0 space-y-3 p-5">
            <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              {t(`eventInfo.group.${group.key}`)}
            </h2>
            {group.fields
              .filter((field) => field.kind === "short" || field.kind === "text")
              .map((field) => {
                const value = details[field.key];
                if (!value) return null;
                return field.kind === "short" ? (
                  <p key={field.key} className="break-words text-base font-semibold">
                    {value}
                  </p>
                ) : (
                  <InformationText key={field.key} text={value} />
                );
              })}
            <div className="space-y-1 empty:hidden">
              {group.fields.map((field) => {
                const value = details[field.key];
                if (
                  !value ||
                  (field.kind !== "phone" && field.kind !== "email" && field.kind !== "url")
                )
                  return null;
                const href =
                  field.kind === "url"
                    ? informationUrl(value)
                    : informationContactHref(field.kind, value);
                if (!href)
                  return (
                    <p key={field.key} className="break-all text-sm">
                      {value}
                    </p>
                  );
                const LinkIcon =
                  field.kind === "phone"
                    ? PhoneIcon
                    : field.kind === "email"
                      ? MailIcon
                      : ExternalLinkIcon;
                return (
                  <a
                    key={field.key}
                    href={href}
                    aria-label={`${t(`eventInfo.detail.${field.key}`)}: ${value}`}
                    {...(field.kind === "url"
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="flex min-h-11 items-center gap-2 text-sm text-brand-ink hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <LinkIcon className="size-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 break-all">
                      {field.kind === "url" ? informationLinkSite(value) : value}
                    </span>
                  </a>
                );
              })}
            </div>
          </section>
        );
      })}
    </Card>
  );
}

export function TournamentDetailsInfo({ details }: { details: TournamentDetails }) {
  const t = useT();
  return TOURNAMENT_DETAIL_GROUPS.filter(
    (group) => group.key === "amenities" || group.key === "fees",
  ).map((group) => {
    const fields = group.fields.filter((field) => details[field.key]);
    if (!fields.length) return null;
    const summary =
      group.key === "fees"
        ? [
            details.feeAmount,
            details.paymentDeadline
              ? t("eventInfo.payBy", { when: eventWhen(details.paymentDeadline) })
              : undefined,
          ]
            .filter(Boolean)
            .join(" · ")
        : fields.map((field) => t(`eventInfo.detail.${field.key}`)).join(", ");
    return (
      <InfoDisclosure
        key={group.key}
        title={t(`eventInfo.group.${group.key}`)}
        summary={summary}
        icon={group.key === "fees" ? BanknoteIcon : AccessibilityIcon}
        open={group.key === "amenities"}
      >
        <dl className="space-y-5">
          {fields.map((field) => {
            const value = details[field.key] ?? "";
            return (
              <div key={field.key} className="space-y-1.5">
                <dt className="text-sm font-semibold">{t(`eventInfo.detail.${field.key}`)}</dt>
                <dd className="min-w-0 text-sm text-muted-foreground">
                  {field.kind === "datetime" ? (
                    <>
                      <time dateTime={value}>{eventWhen(value)}</time>
                      <p className="mt-1 text-xs">{t("eventInfo.localTime")}</p>
                    </>
                  ) : (
                    <InformationText text={value} />
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </InfoDisclosure>
    );
  });
}
